import React, { useState, useEffect, useMemo } from "react";
import {
    Card,
    Row,
    Col,
    Form,
    InputNumber,
    Alert,
    Tooltip,
    Divider,
    Tag,
    Typography,
    Button,
    message,
    Space,
    Tabs,
} from "antd";
import { InfoCircleOutlined, SaveOutlined } from "@ant-design/icons";
import Loadingpage from "../Loadingpage/Loadingpage";
import consts from "../../constant";
import axios from "axios";

const { Title, Text } = Typography;

const DEFAULT_SEGMENTS = [
    {
        segment: 1,
        min: 0,
        max: 25,
        win_percentage: 16,
        loss_percentage: -33,
        percentage: 16,
        title: "Quarter 1",
        desc: "Closed in first 25% of elapsed duration",
        subDesc: "0% – 25% duration elapsed",
        color: "#52c41a",
        tagColor: "success",
    },
    {
        segment: 2,
        min: 25,
        max: 50,
        win_percentage: 40,
        loss_percentage: -58,
        percentage: 40,
        title: "Quarter 2",
        desc: "Closed between 25% and 50% of elapsed duration",
        subDesc: "25% – 50% duration elapsed",
        color: "#faad14",
        tagColor: "warning",
    },
    {
        segment: 3,
        min: 50,
        max: 75,
        win_percentage: 64,
        loss_percentage: -83,
        percentage: 64,
        title: "Quarter 3",
        desc: "Closed between 50% and 75% of elapsed duration",
        subDesc: "50% – 75% duration elapsed",
        color: "#fa541c",
        tagColor: "error",
    },
    {
        segment: 4,
        min: 75,
        max: 100,
        win_percentage: 88,
        loss_percentage: -96,
        percentage: 88,
        title: "Quarter 4",
        desc: "Closed between 75% and 100% of elapsed duration",
        subDesc: "75% – 100% duration elapsed",
        color: "#1890ff",
        tagColor: "processing",
    },
];

const ProfitSegmentSettings = () => {

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [profitDetails, setProfitDetails] = useState({});
    const [activeRole, setActiveRole] = useState("basic");
    const [messageApi, contextHolder] = message.useMessage();
    const token = localStorage.getItem("Jetix");

    const currentValues = Form.useWatch([], form);

    const normalizeRoleValues = (values = {}) => {
        return DEFAULT_SEGMENTS.reduce((normalized, segment) => {
            const winKey = `segment_${segment.segment}_win`;
            const lossKey = `segment_${segment.segment}_loss`;
            normalized[winKey] = values[winKey] === undefined || values[winKey] === null ? values[winKey] : Number(values[winKey]);
            normalized[lossKey] = values[lossKey] === undefined || values[lossKey] === null ? values[lossKey] : Number(values[lossKey]);
            return normalized;
        }, {});
    };

    const changedRoles = useMemo(() => {
        return ['basic', 'vip', 'vvip'].reduce((roles, role) => {
            if (!profitDetails?.[role]) {
                roles[role] = false;
                return roles;
            }

            roles[role] = (
                JSON.stringify(normalizeRoleValues(currentValues?.[role])) !==
                JSON.stringify(normalizeRoleValues(profitDetails?.[role]))
            );
            return roles;
        }, {});
    }, [currentValues, profitDetails]);

    const fetchData = async (role = activeRole) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${consts.BackendUrl}/settings/getProfitDetails`, {
                params: { role },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (data?.success) {
                const results = data.result || {};
                const initialValues = {
                    basic: {},
                    vip: {},
                    vvip: {}
                };

                ['basic', 'vip', 'vvip'].forEach(r => {
                    const roleData = results[r] || [];
                    if (roleData.length > 0) {
                        roleData.forEach(seg => {
                            const winVal = seg.win_percentage !== undefined ? Number(seg.win_percentage) : Number(seg.percentage || 0);
                            let lossVal = seg.loss_percentage;
                            if (lossVal === undefined || lossVal === null) {
                                const def = DEFAULT_SEGMENTS.find(d => d.segment === seg.segment);
                                lossVal = def ? def.loss_percentage : -Math.abs(Number(winVal || 0));
                            } else {
                                lossVal = Number(lossVal);
                                if (lossVal > 0) lossVal = -lossVal;
                            }
                            initialValues[r][`segment_${seg.segment}_win`] = winVal;
                            initialValues[r][`segment_${seg.segment}_loss`] = lossVal;
                        });
                    } else {
                        const fallbackData = Array.isArray(results) && results.length > 0 ? results : DEFAULT_SEGMENTS;
                        fallbackData.forEach((seg, idx) => {
                            const def = DEFAULT_SEGMENTS.find(d => d.segment === seg.segment) || DEFAULT_SEGMENTS[idx] || {};
                            const winVal = seg.win_percentage !== undefined ? Number(seg.win_percentage) : (Number(seg.percentage) || def.win_percentage || 0);
                            let lossVal = seg.loss_percentage !== undefined ? Number(seg.loss_percentage) : (def.loss_percentage ?? -33);
                            if (lossVal > 0) lossVal = -lossVal;
                            initialValues[r][`segment_${seg.segment || idx + 1}_win`] = winVal;
                            initialValues[r][`segment_${seg.segment || idx + 1}_loss`] = lossVal;
                        });
                    }
                });

                form.setFieldsValue(initialValues);
                setProfitDetails(initialValues);
            }

        } catch (error) {
            console.error("Fetch error:", error);
            messageApi.open({
                type: "error",
                content: "Failed to fetch profit segment settings",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (key) => {
        setActiveRole(key);
        fetchData(key);
    };

    useEffect(() => {
        fetchData('basic');
    }, []);

    const onFinish = async (values, role) => {
        setSubmitLoading(true);
        try {
            const payload = DEFAULT_SEGMENTS.map(seg => {
                const rawWin = values[role]?.[`segment_${seg.segment}_win`];
                const rawLoss = values[role]?.[`segment_${seg.segment}_loss`];
                const winNum = rawWin !== undefined && rawWin !== null ? Number(rawWin) : seg.win_percentage;
                let lossNum = rawLoss !== undefined && rawLoss !== null ? Number(rawLoss) : seg.loss_percentage;
                if (lossNum > 0) lossNum = -lossNum;

                return {
                    segment: seg.segment,
                    min: seg.min,
                    max: seg.max,
                    win_percentage: winNum,
                    loss_percentage: lossNum,
                    percentage: winNum
                };
            });

            const { data } = await axios.post(`${consts.BackendUrl}/settings/updateProfitDetails`, {
                role,
                profit_details: payload
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (data?.success) {
                messageApi.open({
                    type: "success",
                    content: "Profit segments updated successfully",
                });
                fetchData(role);
            } else {
                messageApi.open({
                    type: "error",
                    content: data?.message || "Failed to update profit segments",
                });
            }

        } catch (error) {
            messageApi.open({
                type: "error",
                content: error?.response?.data?.message || error.message || "Failed to update profit segments",
            });
        } finally {
            setSubmitLoading(false);
        }
    };

    const renderRolesContent = (roleKey) => (
        <div style={{ marginTop: "20px" }}>
            {/* Headers (hidden on mobile) */}
            <Row gutter={16} className="hide-on-mobile" style={{ paddingBottom: "16px", color: "var(--text-color-secondary, rgba(255, 255, 255, 0.65))", fontWeight: 600 }}>
                <Col xs={0} md={4}>Quarter / Segment</Col>
                <Col xs={0} md={4}>Elapsed Duration</Col>
                <Col xs={0} md={6}>Description</Col>
                <Col xs={0} md={5} style={{ textAlign: "center" }}>
                    Winning % <Tooltip title="Payout % applied if trade is closed early while in profit (e.g. +16%)"><InfoCircleOutlined style={{ color: "#22c55e" }} /></Tooltip>
                </Col>
                <Col xs={0} md={5} style={{ textAlign: "center" }}>
                    Losing % <Tooltip title="Payout % applied if trade is closed early while in loss (e.g. -33% to -96%)"><InfoCircleOutlined style={{ color: "#ef4444" }} /></Tooltip>
                </Col>
            </Row>
            <Divider style={{ margin: "0 0 16px 0", borderColor: "var(--border-color-split, rgba(254, 105, 55, 0.2))" }} />

            {DEFAULT_SEGMENTS.map((seg) => (
                <div key={`${roleKey}_${seg.segment}`}>
                    <Row
                        gutter={16}
                        align="middle"
                        style={{
                            padding: "16px",
                            marginBottom: "16px",
                            borderRadius: "8px",
                            background: "rgba(255, 255, 255, 0.02)",
                            border: "1px solid var(--border-color-split, rgba(254, 105, 55, 0.2))",
                            borderLeft: `4px solid var(--primary-color)`
                        }}
                    >
                        {/* Segment Badge/Name */}
                        <Col xs={24} md={4} style={{ marginBottom: "12px" }}>
                            <Space>
                                <div style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "50%",
                                    background: "var(--primary-color)",
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    fontSize: "14px"
                                }}>
                                    {seg.segment}
                                </div>
                                <Text strong style={{ color: "var(--primary-color)" }}>{seg.title}</Text>
                            </Space>
                        </Col>

                        {/* Interval Tag */}
                        <Col xs={24} md={4} style={{ marginBottom: "12px" }}>
                            <Tag style={{ color: "#fc4420", borderColor: "#fc4420", padding: "4px 12px", backgroundColor: "#000", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                {seg.min}% &ndash; {seg.max}%
                            </Tag>
                        </Col>

                        {/* Description */}
                        <Col xs={24} md={6} style={{ marginBottom: "12px" }}>
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <Text strong style={{ color: "var(--text-color, #fff)", fontSize: "14px" }}>
                                    {seg.desc}
                                </Text>
                                <Text style={{ color: "var(--text-color-secondary, rgba(255, 255, 255, 0.45))", fontSize: "12px" }}>
                                    {seg.subDesc}
                                </Text>
                            </div>
                        </Col>

                        {/* Winning % Input */}
                        <Col xs={12} md={5} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                            <span className="hide-on-desktop" style={{ color: "#22c55e", fontSize: "12px", marginBottom: "4px" }}>Winning %</span>
                            <Form.Item
                                name={[roleKey, `segment_${seg.segment}_win`]}
                                noStyle
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <InputNumber
                                    className="profit-percent-input"
                                    min={0}
                                    max={100}
                                    precision={2}
                                    step={1}
                                    addonBefore="+"
                                    addonAfter="%"
                                    style={{ width: "100%", maxWidth: "130px", color: "#22c55e" }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Losing % Input */}
                        <Col xs={12} md={5} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                            <span className="hide-on-desktop" style={{ color: "#ef4444", fontSize: "12px", marginBottom: "4px" }}>Losing %</span>
                            <Form.Item
                                name={[roleKey, `segment_${seg.segment}_loss`]}
                                noStyle
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <InputNumber
                                    className="profit-percent-input"
                                    min={-100}
                                    max={0}
                                    precision={2}
                                    step={1}
                                    addonAfter="%"
                                    style={{ width: "100%", maxWidth: "130px", color: "#ef4444" }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            ))}
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => {
                        const values = form.getFieldsValue();
                        onFinish(values, roleKey);
                    }}
                    loading={submitLoading}
                    disabled={!changedRoles[roleKey] || submitLoading}
                >
                    Save {roleKey.toUpperCase()} Profit Segments
                </Button>
            </div>
        </div>
    );

    const tabItems = [
        { key: "basic", label: "Basic Role", children: renderRolesContent("basic") },
        { key: "vip", label: "VIP Role", children: renderRolesContent("vip") },
        { key: "vvip", label: "VVIP Role", children: renderRolesContent("vvip") },
    ];

    return (
        <div style={{ paddingBottom: '50px' }}>
            {contextHolder}
            {loading && <Loadingpage />}
            <div className="trade-pattern-page">
                {/* Header Section */}
                <div className="display-2 p-10 trade-pattern-header" style={{ alignItems: "center" }}>
                    <div>
                        <Title level={3} style={{ color: "var(--text-color, #fff)", margin: 0, fontWeight: 600 }}>
                            Early Close Profit &amp; Loss Segments
                        </Title>
                        <Text style={{ color: "var(--text-color-secondary, rgba(255, 255, 255, 0.65))" }}>
                            Configure 4 winning quarter checkpoints and 4 losing quarter checkpoints based on user role and elapsed trade time.
                        </Text>
                    </div>
                </div>

                {/* Info Alert */}
                <div style={{ padding: "0 20px 20px 20px" }}>
                    <Alert
                        message="Separate Winning and Losing Checkpoints"
                        description="Early close uses 4 checkpoints (0-25%, 25-50%, 50-75%, 75-100%). Payout percentages are already final (spread included) and applied directly to the bet amount: Returned Amount = Bet + (Bet × Configured %)."
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined style={{ color: "var(--primary-color)" }} />}
                        style={{
                            borderRadius: "8px",
                            backgroundColor: "rgba(24, 144, 255, 0.1)",
                            border: "1px solid var(--primary-color)",
                            color: "var(--text-color, #fff)"
                        }}
                    />
                </div>

                {/* Configuration Card */}
                <div style={{ padding: "0 20px", }}>
                    <Card
                        title={<Title level={5} style={{ margin: 0, color: "var(--text-color, #fff)" }}>Quarter Checkpoint Configuration (4 Winning + 4 Losing)</Title>}
                        className="table-clr"
                        bordered={false}
                        style={{
                            borderRadius: "12px",
                            background: "var(--component-background, rgba(255, 124, 24, 0.05))",
                            border: "1px solid var(--border-color-split, var(--primary-color))"
                        }}
                        headStyle={{ borderBottom: "1px solid var(--border-color-split, var(--primary-color))" }}
                    >
                        <Form form={form} layout="vertical" onFinish={(v) => onFinish(v, activeRole)}>
                            <Tabs className="custom-tabs" activeKey={activeRole} items={tabItems} onChange={handleTabChange} />
                        </Form>

                        {/* How it works Box */}
                        <Alert
                            message="How Early Close Works"
                            description="When a user closes an active prediction early (>= 30s duration), the system identifies the quarter corresponding to elapsed duration. If currently in profit, the Winning % is applied. If currently in loss, the Losing % is applied directly: Final Return = Bet + (Bet × Configured %)."
                            type="info"
                            showIcon
                            icon={<InfoCircleOutlined style={{ color: "var(--primary-color)" }} />}
                            style={{
                                marginTop: "24px",
                                borderRadius: "8px",
                                backgroundColor: "rgba(255, 124, 24, 0.05)",
                                border: "1px dashed var(--primary-color)",
                                color: "#fff"
                            }}
                        />
                    </Card>

                    {/* Footer Note */}
                    <div style={{ marginTop: "24px" }}>
                        <Alert
                            message={<span ><strong>Note:</strong> Winning percentages represent net profit on bet (+16% means $10 bet returns $11.60). Losing percentages represent net return on bet (-33% means $10 bet returns $6.70, i.e. $3.30 lost; -96% capped means $10 bet returns $0.40).</span>}
                            type="warning"
                            showIcon
                            icon={<InfoCircleOutlined style={{ color: "var(--primary-color)" }} />}
                            style={{
                                borderRadius: "8px",
                                backgroundColor: "rgba(255, 255, 255, 0.02)",
                                border: "1px solid var(--border-color-split, var(--primary-color))",
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfitSegmentSettings;

